<template>
  <section class="product-section">
    <div class="product-row">
      <div class="product-visual">
        <ProductThumb :size="120" :alt="title" />
      </div>
      <div class="product-info">
        <h1 class="title">{{ title }}</h1>
        <p class="desc">{{ desc }}</p>
        <p class="price">{{ price }}</p>
        <div class="qty-row">
          <span class="qty-label">数量</span>
          <div class="qty-ctrl">
            <button type="button" class="qty-btn" @click="decrease" aria-label="减少">
              −
            </button>
            <span class="qty-val">{{ quantity }}</span>
            <button type="button" class="qty-btn" @click="increase" aria-label="增加">
              +
            </button>
          </div>
        </div>
        <p class="superior">上级地址: {{ $utils.shortenAddress(resolvedSuperiorAddress) }}</p>
        <div class="tools">
          <div
            class="buy"
            :class="{ 'buy-disabled': purchasing }"
            role="button"
            @click="buyHandle"
          >
            {{ purchasing ? "处理中..." : "购买" }}
          </div>
          <div
            v-if="showShareButton"
            class="share"
            @click="$emit('share')"
          >
            <span>分享</span>
            <i class="iconfont icon-fenxiang"></i>
          </div>
        </div>
      </div>
    </div>
    
  </section>
</template>

<script>
import ProductThumb from "@/components/ProductThumb.vue";
import Web3 from "web3";
import { Notify, Toast } from "vant";
import {
  buy,
  checkNetwork,
  web3js,
  isChainId,
  sellWineContractInstance,
  getSellWineParent,
  isBuy,
} from "@/contract";
import { submit_order } from "@/api";
import { ensureUsdtAllowanceForSellWine } from "../helpers/winePurchaseAllowance.js";
import { SUPERIOR_ADDRESS_MOCK } from "../constants.js";

export default {
  name: "ProductSection",
  components: {
    ProductThumb,
  },
  props: {
    title: { type: String, required: true },
    desc: { type: String, required: true },
    price: { type: String, required: true },
    linkSuperiorAddress: { type: String, default: "" },
    /** 与 `constants.js` 中 SUPERIOR_ADDRESS_MOCK 一致；仅覆盖时才传入 */
    defaultSuperiorAddress: {
      type: String,
      default: SUPERIOR_ADDRESS_MOCK,
    },
  },
  data() {
    return {
      quantity: 1,
      purchasing: false,
      contractChainParent: "",
      chainParentFetchId: 0,
    };
  },
  computed: {
    accountAddress() {
      return this.$store.state.user.accountAddress;
    },
    resolvedSuperiorAddress() {
      const chain = this.normalizeAddr(this.contractChainParent);
      if (chain) return chain;
      const link = this.normalizeAddr(this.linkSuperiorAddress);
      if (link) return link;
      return this.normalizeAddr(this.defaultSuperiorAddress);
    },
    /** 链上已绑定上级时展示分享（仅 getParent 结果，不含链接/默认占位） */
    showShareButton() {
      return !!this.normalizeAddr(this.contractChainParent);
    },
  },
  watch: {
    accountAddress: {
      immediate: true,
      handler() {
        this.refreshChainParent();
      },
    },
  },
  methods: {
    normalizeAddr(raw) {
      const s = typeof raw === "string" ? raw.trim() : "";
      if (!s || !Web3.utils.isAddress(s)) return "";
      return Web3.utils.toChecksumAddress(s);
    },
    async refreshChainParent() {
      const fetchId = ++this.chainParentFetchId;
      this.contractChainParent = "";
      const acc = this.normalizeAddr(this.accountAddress);
      if (!acc) return;
      try {
        const p = await getSellWineParent(acc);
        console.log(p,'parent');
        if (fetchId !== this.chainParentFetchId) return;
        const parent = this.normalizeAddr(p);
        if (parent) this.contractChainParent = parent;
      } catch (err) {
        if (fetchId !== this.chainParentFetchId) return;
        console.error(err);
        Notify({
          type: "danger",
          message: "读取链上上级地址失败，请稍后重试",
        });
      }
    },
    increase() {
      this.quantity += 1;
    },
    decrease() {
      if (this.quantity > 1) {
        this.quantity -= 1;
      }
    },
    async buyHandle() {
      if (this.purchasing) return;

      const account = this.accountAddress;
      if (!account) {
        Notify({ type: "danger", message: this.$t("logTip") });
        return;
      }
      if (!sellWineContractInstance || !web3js) {
        Notify({
          type: "danger",
          message: this.$t("logTip"),
        });
        return;
      }

      const rawSuperior =
        typeof this.resolvedSuperiorAddress === "string"
          ? this.resolvedSuperiorAddress.trim()
          : "";
      if (!Web3.utils.isAddress(rawSuperior)) {
        Notify({ type: "danger", message: "上级地址无效，请检查后重试" });
        return;
      }
      const parent = Web3.utils.toChecksumAddress(rawSuperior);

      if (!Number.isFinite(this.quantity) || this.quantity < 1) {
        Notify({ type: "danger", message: "购买数量无效" });
        return;
      }

      this.purchasing = true;
      Toast.loading({
        duration: 0,
        forbidClick: true,
      });

      try {
        await checkNetwork(web3js);
        if (!isChainId) {
          Notify({
            type: "danger",
            message: "当前链不正确，请切换网络后重试",
          });
          return;
        }

        let canBuy;
        try {
          canBuy = await isBuy(parent);
        } catch (errIsBuy) {
          const msg =
            typeof errIsBuy?.message === "string"
              ? errIsBuy.message
              : "下单资格校验失败";
          Notify({ type: "danger", message: msg });
          return;
        }
        if (!canBuy) {
          Notify({ type: "danger", message: "当前无法下单，请检查上级地址" });
          return;
        }

        try {
          const { didApprove } = await ensureUsdtAllowanceForSellWine();
          if (didApprove) {
            Notify({
              type: "success",
              message: this.$t("shouquan") + this.$t("success"),
            });
          }
        } catch (errApprove) {
          if (walletUserRejectedTransaction(errApprove)) {
            Notify({ type: "warning", message: "已取消签名" });
          } else {
            Notify({
              type: "danger",
              message: this.$t("shouquan") + this.$t("failed"),
            });
          }
          return;
        }

        let receipt;
        try {
          receipt = await buy(this.quantity, parent);
        } catch (errBuy) {
          if (walletUserRejectedTransaction(errBuy)) {
            Notify({ type: "warning", message: "已取消签名" });
          } else {
            const buyMsg =
              typeof errBuy?.message === "string"
                ? errBuy.message
                : String(errBuy ?? "");
             console.log(buyMsg,'error buy????')   
            Notify({
              type: "danger",
              message: "买入失败",
            });
          }
          return;
        }

        try {
          await submit_order({
            tx_hash: receipt.hash,
            wallet: account,
            parent,
            amount: receipt.amount,
            price: receipt.price,
            number: receipt.number,
            created: receipt.timestamp,
          });
        } catch (errSubmit) {
          console.log(errSubmit);
        }

        Notify({ type: "success", message: "购买成功" });
        await this.refreshChainParent();
      } finally {
        Toast.clear();
        this.purchasing = false;
      }
    },
  },
};

function walletUserRejectedTransaction(err) {
  const code = err?.code;
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    code === 4001 ||
    code === "ACTION_REJECTED" ||
    msg.includes("user denied") ||
    msg.includes("user rejected") ||
    msg.includes("denied transaction signature")
  );
}
</script>

<style lang="scss" scoped>
.product-section {
  padding: 0 0 32px;
}

.product-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.product-visual {
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.title {
  margin: 0 0 12px;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.35;
  color: #1a1a1a;
}

.desc {
  margin: 0 0 16px;
  font-size: 22px;
  line-height: 1.5;
  color: #8c8c8c;
}

.price {
  margin: 0 0 20px;
  font-size: 40px;
  font-weight: 600;
  color: #ff4d4f;
  letter-spacing: 0.02em;
}

.qty-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.qty-label {
  font-size: 24px;
  color: #333;
}

.qty-ctrl {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.qty-btn {
  width: 56px;
  height: 48px;
  border: none;
  background: #fafafa;
  font-size: 28px;
  line-height: 1;
  color: #333;
  cursor: pointer;
  padding: 0;
}

.qty-btn:active {
  background: #f0f0f0;
}

.qty-val {
  min-width: 48px;
  text-align: center;
  font-size: 24px;
  color: #333;
}

.superior {
  margin: 0;
  font-size: 22px;
  line-height: 1.4;
  color: #8c8c8c;
  word-break: break-all;
}
.tools{
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  .buy{
    width: 300px;
    height: 60px;
    line-height: 60px;
    background: #E6C57F;
    color: #000;
    text-align: center;
    font-size: 3.6vw;
    border-radius: 8px;
    cursor: pointer;
    &.buy-disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }
  .share{
    height: 60px;
    display: flex;
    align-items: center;
    font-size: 2.6vw;
    i{
      font-size: 5.2vw;
      margin-left: 6px;
    }
  }
}

</style>
